import { Injectable, Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';

import type { FeatureAnnouncement, FeatureInteractions } from '../../models';
import {
  LoadFeatureAnnouncements,
  MarkAllFeatureAnnouncementsSeen,
  MarkFeatureAnnouncementSeen,
} from '../actions';
import { FeatureAnnouncementLoadStatus } from '../feature-announcement.state';
import { FeatureAnnouncementSelectors } from '../selectors';

@Injectable({ providedIn: 'root' })
export class FeatureAnnouncementStoreService {
  constructor(private readonly store: Store) {}

  get features(): Signal<FeatureAnnouncement[]> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.features);
  }

  get interactions(): Signal<FeatureInteractions> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.interactions);
  }

  get status(): Signal<FeatureAnnouncementLoadStatus> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.status);
  }

  get loaded(): Signal<boolean> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.loaded);
  }

  get activeFeatures(): Signal<FeatureAnnouncement[]> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.activeFeatures);
  }

  get unseenFeatures(): Signal<FeatureAnnouncement[]> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.unseenFeatures);
  }

  get unseenCount(): Signal<number> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.unseenCount);
  }

  get hasUnseen(): Signal<boolean> {
    return this.store.selectSignal(FeatureAnnouncementSelectors.hasUnseen);
  }

  isSeen(featureKey: string): Signal<boolean> {
    return this.store.selectSignal(
      FeatureAnnouncementSelectors.isSeen(featureKey),
    );
  }

  isTourCompleted(featureKey: string): Signal<boolean> {
    return this.store.selectSignal(
      FeatureAnnouncementSelectors.isTourCompleted(featureKey),
    );
  }

  featureByKey(featureKey: string): Signal<FeatureAnnouncement | undefined> {
    return this.store.selectSignal(
      FeatureAnnouncementSelectors.featureByKey(featureKey),
    );
  }

  unseenTargetFeature(target: string): Signal<FeatureAnnouncement | undefined> {
    return this.store.selectSignal(
      FeatureAnnouncementSelectors.unseenTargetFeature(target),
    );
  }

  unseenNavTargetFeature(
    target: string,
  ): Signal<FeatureAnnouncement | undefined> {
    return this.store.selectSignal(
      FeatureAnnouncementSelectors.unseenNavTargetFeature(target),
    );
  }

  @Dispatch()
  loadFeatureAnnouncements = () => new LoadFeatureAnnouncements();

  @Dispatch()
  markFeatureAnnouncementSeen = (featureKey: string, tourCompleted = false) =>
    new MarkFeatureAnnouncementSeen(featureKey, tourCompleted);

  @Dispatch()
  markAllFeatureAnnouncementsSeen = () => new MarkAllFeatureAnnouncementsSeen();
}
